import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Loader2, Send, Users, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../ui/dialog';

export default function FeedbackCampaignCard({ API }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('admin_token')}`
  });

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/feedback-campaign/preview`, { headers: authHeaders() });
      setPreview(data);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load campaign info');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPreview(); }, []);

  const sendCampaign = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/feedback-campaign/send`, {}, { headers: authHeaders() });
      setResult(data);
      toast.success(`Feedback campaign sent: ${data.sent} emails delivered`);
      setConfirmOpen(false);
      loadPreview();
    } catch (e) {
      console.error(e);
      toast.error('Campaign failed to send');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
    } catch { return iso; }
  };

  return (
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4 sm:p-6 overflow-hidden" data-testid="feedback-campaign-card">
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
          <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-[#1A1A1A] mb-1">Feedback Email Campaign</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Email all registered users a one-time feedback request with a link to the survey at <span className="font-medium">/feedback</span>.
          </p>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : preview ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 mb-4">
                <div className="bg-white rounded-lg p-2.5 sm:p-3 border border-amber-100 min-w-0">
                  <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-1">
                    <Users className="w-3 h-3" /> Eligible
                  </div>
                  <p className="text-xl font-bold text-amber-700" data-testid="campaign-eligible-count">{preview.eligible_users}</p>
                </div>
                <div className="bg-white rounded-lg p-2.5 sm:p-3 border border-amber-100 min-w-0">
                  <div className="text-[11px] text-muted-foreground mb-1">Total users</div>
                  <p className="text-xl font-bold text-[#1A1A1A]">{preview.total_users}</p>
                </div>
                <div className="bg-white rounded-lg p-2.5 sm:p-3 border border-amber-100 min-w-0 col-span-2 sm:col-span-1">
                  <div className="text-[11px] text-muted-foreground mb-1">Unsubscribed</div>
                  <p className="text-xl font-bold text-muted-foreground">{preview.unsubscribed}</p>
                </div>
              </div>

              {preview.last_campaign && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4" data-testid="last-campaign-info">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                  Last campaign: <span className="font-medium text-foreground">{formatDate(preview.last_campaign.started_at)}</span>
                  {' · '}sent to {preview.last_campaign.sent} user{preview.last_campaign.sent !== 1 ? 's' : ''}
                  {preview.last_campaign.failed > 0 && ` · ${preview.last_campaign.failed} failed`}
                </div>
              )}

              <Button
                onClick={() => setConfirmOpen(true)}
                disabled={preview.eligible_users === 0}
                className="bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto h-auto py-2.5 whitespace-normal text-left leading-snug"
                data-testid="send-feedback-campaign-btn"
              >
                <Send className="w-4 h-4 mr-2 shrink-0" />
                <span>Send Feedback Request to {preview.eligible_users} User{preview.eligible_users !== 1 ? 's' : ''}</span>
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {/* Confirmation Modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md" data-testid="campaign-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Send to {preview?.eligible_users} users?
            </DialogTitle>
            <DialogDescription className="pt-2">
              This will email <span className="font-semibold text-foreground">{preview?.eligible_users}</span> registered users a personalized feedback request
              with a link to the survey. Users who unsubscribed from marketing emails will be skipped.
            </DialogDescription>
          </DialogHeader>

          {result && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-green-800">✓ Campaign complete</p>
              <p className="text-green-700 text-xs mt-1">
                Sent: {result.sent} · Failed: {result.failed} · Skipped: {result.skipped}
              </p>
            </div>
          )}

          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={sending}>
              Cancel
            </Button>
            <Button
              onClick={sendCampaign}
              disabled={sending}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="confirm-send-campaign-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {sending ? 'Sending…' : 'Yes, send now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
